import { getValuationAsOf } from './valuations-table.js';
import { getNextCertNumber } from './string-utils.js';

/**
 * Transaction Custom Columns
 *
 * Column-definition shape and naming conventions deliberately mirror
 * src/admin/custom-reports/cap-table-custom-column-storage-keys.js /
 * cap-table-custom-column-value.js (Cap Table Custom Columns, "CTCC") --
 * added 2026-08-05 per Stuart's review of BW's Gift FMV column (Round Four,
 * see docs/cap-table-custom-columns.md for the CTCC reference this mirrors).
 *
 * What's shared with CTCC: the column-definition shape
 * ({ key, label, source, format, includeInExport, showInTableUI }), the
 * `source` field as the authoritative "how is this resolved / is it
 * editable" signal, and `''`/`'custom'` meaning "manual, directly editable,
 * stored under its own key" -- same vocabulary, same philosophy, same
 * default-true convention for includeInExport/showInTableUI.
 *
 * What's NOT shared, deliberately: WHERE the value lives. CTCC's manual
 * columns store exactly one value per SHAREHOLDER, at userClient[col.key] --
 * one slot per person, no matter how many transactions they have. A
 * transaction-level custom column (Gift FMV, in particular) needs one value
 * per TRANSACTION -- a shareholder can have multiple Gift Transfer events,
 * each with its own fair-market-value at the moment of that specific gift.
 * Reusing userClient[col.key] storage here would collapse every one of a
 * shareholder's gifts onto a single shared value -- a real data-integrity
 * regression, not just a style mismatch. So this module borrows CTCC's
 * DEFINITION shape and vocabulary only; storage stays exactly where every
 * other transaction field already lives -- the transaction record itself,
 * keyed by col.key (see TransactionService.js's _setOptional/_recordToRow,
 * and TransactionColumnRegistry.js's accessor: (txn) => txn[col.key]).
 *
 * Only the manual ('' / 'custom') source is implemented today -- there is no
 * transaction-level equivalent yet of CTCC's formula:/aggregate:/
 * calculator:/expression:/classifier:/user.<field> resolvers. A column
 * definition with any other source is simply filtered out (not rendered,
 * not editable) rather than throwing -- clientData.transaction_custom_columns
 * is meant to be forward-compatible with source types this module doesn't
 * resolve yet, the same way CTCC itself grew its source types incrementally
 * from an initial manual-only base.
 *
 * Rendering (2026-08-21 update): TransactionColumnRegistry.js merges these
 * definitions into its column list (so a custom column participates in
 * getVisibleColumns()/getExportColumns() like any built-in one).
 * transaction-table-renderer.js's formatCell() now checks col._isCustom
 * (set below) ahead of its key-based switch, so any number of custom columns
 * render as inline-editable cells generically -- ledgers.js's
 * renderLedgerCustomColumnListeners() reads each cell's data-col-key rather
 * than a fixed field name. shared_assets/index.js (Events & Transactions)
 * has its own separate, equally generic implementation of the same pattern.
 */

/** Mirrors isManualCapTableCustomColumnSource() in cap-table-custom-column-storage-keys.js. */
export function isManualTransactionCustomColumnSource(source) {
	return source == null || source === '' || source === 'custom';
}

/** Mirrors usesCtccKeyStorage() -- same check, operating on a column definition object. */
export function usesTransactionCustomColumnKeyStorage(col) {
	return isManualTransactionCustomColumnSource(col?.source);
}

/**
 * The canonical storage key for a manual transaction custom column: col.key,
 * used verbatim as a property name on the transaction record itself (never
 * on a shareholder/userClient record -- see file header). Mirrors
 * getCapTableCustomColumnClientStorageKey()'s shape, deliberately not its
 * storage target.
 */
export function getTransactionCustomColumnStorageKey(col) {
	return col.key;
}

/** Mirrors listCapTableCustomColumns(). */
export function listTransactionCustomColumns(clientData) {
	return (clientData?.transaction_custom_columns || []).filter((col) => col && col.key);
}

/**
 * Builds TransactionColumnRegistry-shaped column definitions for every
 * manual transaction custom column defined on this client. Non-manual
 * sources are filtered out (see file header).
 *
 * Two more optional per-column properties (2026-09-10): `excludedClasses`
 * (share classes this column doesn't apply to) and `autoPopulate` (a resolver
 * key, see getCustomColumnAutoPopulateValue below) -- both UI-only.
 *
 * showInTableUI/includeInExport default to true (mirrors CTCC's own
 * documented defaults), overridable per-column via the SAME mechanism every
 * built-in STANDARD_COLUMNS entry already uses --
 * clientData.transaction_columns_config[col.key] -- rather than inventing a
 * second override path just for custom columns.
 *
 * @param {Object} clientData
 * @returns {Array} Column definitions, ready to concat onto STANDARD_COLUMNS.
 */
export function buildTransactionCustomColumnDefinitions(clientData) {
	return listTransactionCustomColumns(clientData)
		.filter((col) => usesTransactionCustomColumnKeyStorage(col))
		.map((col) => ({
			key: col.key,
			defaultLabel: col.label || col.key,
			format: col.format || 'text',
			accessor: (txn) => txn[getTransactionCustomColumnStorageKey(col)],
			visibility: 'conditional',
			visibilityRule: (config) => {
				// An explicit transaction_columns_config[col.key].visible entry
				// wins outright, same precedence every built-in column already
				// gives an explicit config override; otherwise fall back to the
				// column definition's own showInTableUI (CTCC convention:
				// default true, explicit false hides it).
				const override = config._colConfig[col.key]?.visible;
				if (override !== undefined) return override;
				return col.showInTableUI !== false;
			},
			includeInExport: col.includeInExport !== false,
			_isCustom: true,
		}));
}

/** True when col applies to shareClass; empty/absent excludedClasses means "everywhere". */
export function isCustomColumnApplicableToClass(col, shareClass) {
	if (!col?.excludedClasses || !col.excludedClasses.length) return true;
	return !col.excludedClasses.includes(shareClass);
}

// Auto-populate resolvers for the Custom Fields section, keyed by col.autoPopulate.
// Suggests a starting value only -- the field stays a normal editable column.
// current_valuation resolves as of the transaction's own date, not today's.
// next_cert_number: `context` is shared across every column in one render
// pass (see renderCustomFieldsFormSection) so two cert fields in the same
// modal (e.g. new sender + new receiver cert) suggest sequential numbers
// instead of colliding on the same one.
const CUSTOM_COLUMN_AUTO_POPULATE_RESOLVERS = {
	current_valuation: (clientData, shareClass, asOfUnixSeconds) => {
		const val = getValuationAsOf(clientData, shareClass, asOfUnixSeconds);
		return val != null ? String(val) : '';
	},
	next_cert_number: (clientData, shareClass, _asOfUnixSeconds, context) => {
		const base = getNextCertNumber(clientData, shareClass);
		if (!base) return '';
		const offset = context.certOffset || 0;
		context.certOffset = offset + 1;
		if (!offset) return base;
		const match = base.match(/(\d+)$/);
		return match ? base.replace(match[1], String(Number(match[1]) + offset)) : base;
	},
};

/** Suggested starting value for a custom column's input, or '' when col.autoPopulate is unset/unknown. */
export function getCustomColumnAutoPopulateValue(col, clientData, shareClass, asOfUnixSeconds, context = {}) {
	const resolver = col?.autoPopulate && CUSTOM_COLUMN_AUTO_POPULATE_RESOLVERS[col.autoPopulate];
	return resolver ? resolver(clientData, shareClass, asOfUnixSeconds, context) : '';
}
